import { useState, useEffect, useMemo, createContext, useContext } from "react";
import { Sun, Moon, RefreshCw, Filter, FileDown } from "lucide-react";
import { exportOrcamentoDocx, exportOrcamentoXlsx } from "./exportOrcamentoDocx";
import { useNavigate } from "react-router-dom";
import { GaugeChart } from "../../componets/GaugeChart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from "recharts";

import {
  getDadosLancamentosDash,
  getDadosOrcamento,
  getRoleFromToken,
  getLancamentosRealizado,
} from "../../api";
import {
  DARK_THEME_ORCAMENTO,
  LIGHT_THEME_ORCAMENTO,
} from "../../componets/temas";
import SideBarNavOrcamento from "../../componets/SideBarNavOrcamento";
// ─── CONTROLE DE ACESSO POR DEPARTAMENTO ──────────────────────────────────────
//
// Fonte única de verdade das permissões: ROLE_SCOPES (mais abaixo). Cada role
// tem uma lista de "escopos" — regras de {grupos, categorias, departamentos}
// combinadas por E (AND) dentro de um escopo, e por OU (OR) entre escopos da
// mesma role. Um registro é visível para o usuário se casar com QUALQUER um
// dos escopos da sua role. Roles sem entrada em ROLE_SCOPES (ex: ADMIN) não
// têm restrição alguma — acesso total, em qualquer dimensão.
//
// Essa mesma estrutura alimenta tanto o filtro final dos dados (KPIs/gráficos)
// quanto o cálculo das opções disponíveis em cada dropdown — ver
// `opcoesDisponiveis` dentro do componente `FinanceiroDashboard`.

// Lista de categorias Facilities reutilizada em dois roles
const FACILITIES_CATS = [
  "2.6.01 Aluguel",
  "2.6.02 Água e Esgoto",
  "2.6.03 Energia Elétrica",
  "2.6.04 Segurança Predial",
  "2.6.05 Seguro Predial",
  "2.6.06 Telefonia",
  "2.6.07 Internet",
  "2.6.08 Serviços Gerais",
  "2.6.09 Copa e Cozinha",
  "2.6.10 Material de Limpeza",
  "2.6.11 Assinaturas e Sistemas",
  "2.6.12 Taxas Administrativas",
  "2.6.13 Frete Administrativo",
  "2.6.14 Cursos e Treinamentos",
  "2.6.15 Ações Sociais",
  "2.6.16 Ações Motivacionais",
  "2.6.17 Aluguel de Equipamentos",
  "2.6.18 Coffee Break",
  "2.6.19 Uniforme e Kit Boas-Vindas",
  "2.6.20 Despesa Administrativa Variável",
  "2.6.21 Frete Administrativo",
  "2.6.22 Custos variáveis",
  "2.7.03 Material de Escritório e Impressos",
  "2.7.04 Móveis e Utensílios",
];

// Departamento "base" de cada role simples de departamento único/fixo. Usado
// tanto para montar ROLE_SCOPES quanto para resolver o departamento de
// coordenadores (ex: "COORDENADOR_RH" reaproveita o departamento da role "RH").
const DEPARTAMENTO_POR_ROLE = {
  CONSELHO: ["CONSELHO"],
  MARKETING: ["DIRETORIA MARKETING"],
  TI: ["DESENVOLVIMENTO TECNOLÓGICO", "OPERAÇÃO"],
  ADMFIN: ["DIRETORIA ADMINISTRATIVO FINANCEIRO"],
  NEGOCIOS: ["DIRETORIA DE DESENVOLVIMENTO DE NEGÓCIOS"],
  EXECUTIVA: ["DIRETORIA EXECUTIVA"],
  QUALIDADE: ["DIRETORIA DE PROCESSOS E QUALIDADE"],
  RH: ["DIRETORIA DE PESSOAS E CULTURA"],
  JURIDICO: ["DIRETORIA JURÍDICA"],
  OPERACAO: ["OPERAÇÃO"],
  ASSISTENTEOPERACAO: ["OPERAÇÃO"],
  COMERCIAL: ["DIRETORIA COMERCIAL"],
  VIAGEMCOMERCIAL: ["DIRETORIA COMERCIAL"],
  FACILITIES: ["DIRETORIA ADMINISTRATIVO FINANCEIRO"],
  "PESSOA&CULTURA": ["DIRETORIA DE PESSOAS E CULTURA"],
  GESTOR_ESTRATEGICO: [
    "DIRETORIA JURÍDICA",
    "DIRETORIA DE PROCESSOS E QUALIDADE",
    "DIRETORIA EXECUTIVA",
  ],
};

// Escopos de permissão por role. Cada entrada é uma lista de escopos
// {grupos?, categorias?, departamentos?} unidos por OR; dentro de um escopo,
// as dimensões presentes são combinadas por AND. Omitir uma dimensão libera
// ela totalmente dentro daquele escopo (ex: VIAGENS não restringe categoria
// nem departamento, só o grupo).
const ROLE_SCOPES = {
  CONSELHO: [{ departamentos: DEPARTAMENTO_POR_ROLE.CONSELHO }],
  MARKETING: [{ departamentos: DEPARTAMENTO_POR_ROLE.MARKETING }],
  TI: [{ departamentos: DEPARTAMENTO_POR_ROLE.TI }],
  ADMFIN: [{ departamentos: DEPARTAMENTO_POR_ROLE.ADMFIN }],
  NEGOCIOS: [{ departamentos: DEPARTAMENTO_POR_ROLE.NEGOCIOS }],
  EXECUTIVA: [{ departamentos: DEPARTAMENTO_POR_ROLE.EXECUTIVA }],
  QUALIDADE: [{ departamentos: DEPARTAMENTO_POR_ROLE.QUALIDADE }],
  RH: [{ departamentos: DEPARTAMENTO_POR_ROLE.RH }],
  JURIDICO: [{ departamentos: DEPARTAMENTO_POR_ROLE.JURIDICO }],
  OPERACAO: [{ departamentos: DEPARTAMENTO_POR_ROLE.OPERACAO }],
  ASSISTENTEOPERACAO: [{ departamentos: DEPARTAMENTO_POR_ROLE.ASSISTENTEOPERACAO }],
  COMERCIAL: [{ departamentos: DEPARTAMENTO_POR_ROLE.COMERCIAL }],
  GESTOR_ESTRATEGICO: [{ departamentos: DEPARTAMENTO_POR_ROLE.GESTOR_ESTRATEGICO }],

  "PESSOA&CULTURA": [
    {
      categorias: [
        "2.4.04 Brindes - Clientes e Parceiros",
        "2.6.16 Ações Motivacionais",
      ],
      departamentos: DEPARTAMENTO_POR_ROLE["PESSOA&CULTURA"],
    },
  ],

  FACILITIES: [
    {
      categorias: FACILITIES_CATS,
      departamentos: DEPARTAMENTO_POR_ROLE.FACILITIES,
    },
  ],

  VIAGENS: [{ grupos: ["2.5 Despesas de Viagens"] }],

  VIAGEMCOMERCIAL: [
    {
      grupos: ["2.5 Despesas de Viagens"],
      departamentos: DEPARTAMENTO_POR_ROLE.VIAGEMCOMERCIAL,
    },
  ],

  // VIAGEMFACILITIES acumula dois escopos independentes (OR): Viagens, com
  // acesso a todos os departamentos; e Administrativas Gerais (categorias
  // Facilities), restrito ao departamento financeiro.
  VIAGEMFACILITIES: [
    { grupos: ["2.5 Despesas de Viagens"] },
    {
      grupos: ["2.6 Administrativas Gerais"],
      categorias: FACILITIES_CATS,
      departamentos: ["DIRETORIA ADMINISTRATIVO FINANCEIRO"],
    },
  ],
};

const ROLE_REMOVER_FOLHA = {
  COORDENADOR: ["2.1 Salários e Encargos", "2.2 Benefícios"],
};

const isMonthInFilters = (key, filtMes) => {
  if (filtMes.includes("all")) return true;
  if (!key) return false;
  if (filtMes.includes(key)) return true;

  const mNum = parseInt(key.split("/")[0], 10);
  if (filtMes.includes("Q1") && mNum >= 1 && mNum <= 3) return true;
  if (filtMes.includes("Q2") && mNum >= 4 && mNum <= 6) return true;
  if (filtMes.includes("Q3") && mNum >= 7 && mNum <= 9) return true;
  if (filtMes.includes("Q4") && mNum >= 10 && mNum <= 12) return true;

  return false;
};
// ─── CONTEXT ──────────────────────────────────────────────────────────────────

const ThemeContext = createContext(LIGHT_THEME_ORCAMENTO);

const parseBRL = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return v;

  const s = String(v).trim();

  if (s.includes(",")) {
    const n = parseFloat(s.replace(/\./g, "").replace(",", "."));
    return isNaN(n) ? null : n;
  }

  if (s.includes(".")) {
    const afterDot = s.split(".").pop();
    if (afterDot.length === 3) {
      const n = parseFloat(s.replace(/\./g, ""));
      return isNaN(n) ? null : n;
    }
    return parseFloat(s);
  }

  return parseFloat(s);
};

const MESES_PT = {
  janeiro: 1,
  fevereiro: 2,
  março: 3,
  marco: 3,
  abril: 4,
  maio: 5,
  junho: 6,
  julho: 7,
  agosto: 8,
  setembro: 9,
  outubro: 10,
  novembro: 11,
  dezembro: 12,
};

const toMonthKey = (d) => {
  if (!d) return null;
  const s = String(d).trim();

  const slashParts = s.split("/");
  if (slashParts.length === 3) return `${slashParts[1]}/${slashParts[2]}`;
  if (slashParts.length === 2) {
    const nomeMes = MESES_PT[slashParts[0].toLowerCase()];
    if (nomeMes) return `${String(nomeMes).padStart(2, "0")}/${slashParts[1]}`;
    return s;
  }

  const nomeMes = MESES_PT[s.toLowerCase()];
  if (nomeMes) return String(nomeMes).padStart(2, "0");

  return s;
};

const monthNumFromKey = (key) => (key ? key.split("/")[0] : null);

const monthLabel = (key) => {
  if (!key) return key;
  const [mm, yyyy] = key.split("/");
  const months = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];
  const label = months[parseInt(mm, 10) - 1] || mm;
  return yyyy ? `${label} ${yyyy}` : label;
};

const fmt = (v) =>
  v == null
    ? "-"
    : new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v);

const fmtShort = (v) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(2)} mil`;
  return fmt(v);
};

// ─── UI ATOMS ─────────────────────────────────────────────────────────────────

function Select({ label, options, value, onChange, disabled = false }) {
  const C = useContext(ThemeContext);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 190,
      }}
    >
      <label
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: C.text,
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        {label}
        {disabled && (
          <span
            title="Filtro restrito ao seu departamento"
            style={{ fontSize: 11, color: C.amber, lineHeight: 1 }}
          >
            🔒
          </span>
        )}
      </label>
      <select
        value={value}
        onChange={(e) => !disabled && onChange(e.target.value)}
        disabled={disabled}
        style={{
          background: disabled ? C.inputDisabled : C.cardBg,
          color: disabled ? C.muted : C.text,
          border: `1px solid ${disabled ? C.amber + "55" : C.border}`,
          borderRadius: 6,
          padding: "10px 65px 10px 65px",
          fontSize: 13,
          fontFamily: "inherit",
          appearance: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238B90A0' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 10px center",
          outline: "none",
          opacity: disabled ? 0.7 : 1,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function MultiSelect({ label, options, value, onChange, disabled = false }) {
  const C = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [isOpen]);

  const toggleOption = (optValue) => {
    if (disabled) return;
    if (optValue === "all") {
      onChange(["all"]);
    } else {
      const newValue = value.includes(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value.filter((v) => v !== "all"), optValue];
      onChange(newValue.length === 0 ? ["all"] : newValue);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 190,
        position: "relative",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <label
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: C.text,
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        {label}
        {disabled && (
          <span
            title="Filtro restrito ao seu perfil de acesso"
            style={{ fontSize: 11, color: C.amber, lineHeight: 1 }}
          >
            🔒
          </span>
        )}
      </label>

      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          background: disabled ? C.inputDisabled : C.cardBg,
          color: disabled ? C.muted : C.text,
          border: `1px solid ${disabled ? C.amber + "55" : C.border}`,
          borderRadius: 6,
          padding: "10px 65px",
          fontSize: 13,
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "36px",
          opacity: disabled ? 0.7 : 1,
        }}
      >
        <span
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 150,
          }}
        >
          {value.includes("all") ? "Selecione" : `${value.length} selecionadas`}
        </span>
        {!disabled && (
          <span style={{ fontSize: 10, color: C.muted }}>
            {isOpen ? "▲" : "▼"}
          </span>
        )}
      </div>

      {isOpen && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: C.cardBg,
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            marginTop: 4,
            padding: "8px 0",
            maxHeight: 500,
            overflowY: "auto",
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          }}
        >
          {options.map((o) => (
            <div
              key={o.value}
              onClick={() => toggleOption(o.value)}
              style={{
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.hover)}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                readOnly
                style={{ accentColor: C.crimson, cursor: "pointer" }}
              />
              <span
                style={{
                  fontSize: 13,
                  color: value.includes(o.value) ? C.text : C.muted,
                }}
              >
                {o.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, accent = false, large = false }) {
  const C = useContext(ThemeContext);
  return (
    <div
      style={{
        background: C.cardBg,
        border: `1px solid ${accent ? C.crimson : C.border}`,
        borderRadius: 10,
        padding: large ? "20px 28px" : "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        boxShadow: accent ? `0 0 24px ${C.crimson}33` : "none",
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.14em",
          color: C.muted,
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: large ? 26 : 20,
          fontWeight: 700,
          color: accent ? C.crimson : C.text,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  const C = useContext(ThemeContext);
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: C.tooltipBg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
      }}
    >
      <p style={{ margin: "0 0 6px 0", fontSize: 11, color: C.muted }}>
        {label}
      </p>
      {payload.map((p) => (
        <p
          key={p.name}
          style={{
            margin: "2px 0",
            fontSize: 13,
            color: p.color,
            fontWeight: 600,
          }}
        >
          {p.name}: {fmtShort(p.value)}
        </p>
      ))}
    </div>
  );
}

function Spinner() {
  const C = useContext(ThemeContext);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: 320,
        gap: 16,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          border: `3px solid ${C.border}`,
          borderTop: `3px solid ${C.crimson}`,
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <span style={{ color: C.muted, fontSize: 13 }}>Carregando dados…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  const C = useContext(ThemeContext);
  return (
    <div
      style={{
        background: C.deepBg,
        border: `1px solid ${C.danger}55`,
        borderRadius: 10,
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 24,
      }}
    >
      <span style={{ color: C.danger, fontSize: 13 }}>⚠️ {message}</span>
      <button
        onClick={onRetry}
        style={{
          background: C.crimson,
          color: "#fff",
          border: "none",
          borderRadius: 6,
          padding: "6px 14px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Tentar novamente
      </button>
    </div>
  );
}

// ─── THEME TOGGLE BUTTON ──────────────────────────────────────────────────────

function ThemeToggle({ isLight, onToggle }) {
  return (
    <button
      onClick={onToggle}
      title={isLight ? "Mudar para tema escuro" : "Mudar para tema claro"}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: isLight ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)",
        border: `1px solid ${isLight ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`,
        borderRadius: 8,
        padding: "7px 13px",
        cursor: "pointer",
        color: "#fff",
        fontSize: 12,
        fontWeight: 600,
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = isLight
          ? "rgba(255,255,255,0.18)"
          : "rgba(0,0,0,0.22)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = isLight
          ? "rgba(255,255,255,0.1)"
          : "rgba(0,0,0,0.12)")
      }
    >
      {isLight ? <Sun size={14} /> : <Moon size={14} />}
      {isLight ? "Tema Escuro" : "Tema Claro"}
    </button>
  );
}

// ─── DEPARTMENT DRAWER ────────────────────────────────────────────────────────

function DepartmentDrawer({ dep, lancamentos, onClose }) {
  const C = useContext(ThemeContext);
  const [expandedGrupos, setExpandedGrupos] = useState({});

  if (!dep) return null;

  const parseDate = (d) => {
    if (!d) return 0;
    const [dd, mm, yyyy] = String(d).split("/");
    return new Date(`${yyyy}-${mm}-${dd}`).getTime();
  };

  const rows = lancamentos
    .filter((r) => (r.Departamento || "-") === dep)
    .sort(
      (a, b) =>
        parseDate(b["Data (completa)"]) - parseDate(a["Data (completa)"]),
    );

  const grupoMap = {};
  rows.forEach((r) => {
    const grupo = r.Grupo || "Sem Grupo";
    if (!grupoMap[grupo]) grupoMap[grupo] = [];
    grupoMap[grupo].push(r);
  });

  const grupos = Object.entries(grupoMap)
    .map(([grupo, items]) => {
      const total = items.reduce((s, r) => {
        const v = typeof r.Valor === "number" ? r.Valor : parseBRL(r.Valor);
        return s + (v || 0);
      }, 0);
      return { grupo, items, total }; // Aqui definimos "grupo"
    })
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

  const totalDep = grupos.reduce((s, c) => s + c.total, 0);

  const toggleGrupo = (grupo) =>
    setExpandedGrupos((prev) => ({ ...prev, [grupo]: !prev[grupo] }));

  const expandAll = () =>
    setExpandedGrupos(Object.fromEntries(grupos.map((c) => [c.grupo, true])));
  const collapseAll = () => setExpandedGrupos({});
  const anyOpen = grupos.some((c) => expandedGrupos[c.grupo]);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "#00000088",
          zIndex: 100,
          backdropFilter: "blur(2px)",
        }}
      />

      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(850px, 96vw)",
          background: C.drawerBg,
          border: `1px solid ${C.border}`,
          borderRight: "none",
          zIndex: 101,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            background: C.panelBg,
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                color: C.muted,
                textTransform: "uppercase",
              }}
            >
              Detalhamento · Realizado
            </p>
            <h2
              style={{
                margin: "4px 0 0",
                fontSize: 16,
                fontWeight: 700,
                color: C.text,
              }}
            >
              {dep}
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: 12, color: C.muted }}>
              {grupos.length} grupo{grupos.length !== 1 ? "s" : ""}
              <span style={{ margin: "0 8px", color: C.border }}>·</span>
              {rows.length} lançamento{rows.length !== 1 ? "s" : ""}
              <span
                style={{
                  marginLeft: 12,
                  fontWeight: 700,
                  color: totalDep < 0 ? C.danger : C.success,
                }}
              >
                Total: R$ {fmt(totalDep * -1)}
              </span>
            </p>
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <button
              onClick={anyOpen ? collapseAll : expandAll}
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.muted,
                fontSize: 11,
                fontWeight: 600,
                padding: "5px 10px",
                cursor: "pointer",
              }}
            >
              {anyOpen ? "− Recolher" : "+ Expandir"}
            </button>
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                color: C.muted,
                fontSize: 18,
                width: 34,
                height: 34,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {grupos.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted }}>
              Nenhum lançamento encontrado.
            </div>
          ) : (
            grupos.map(({ grupo, items, total }, gi) => {
              // CORREÇÃO: Usando 'grupo' em vez de 'cat'
              const isOpen = !!expandedGrupos[grupo];
              const corTotal = total < 0 ? C.danger : C.success;

              return (
                <div
                  key={grupo}
                  style={{ borderBottom: `1px solid ${C.border}` }}
                >
                  <div
                    onClick={() => toggleGrupo(grupo)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "13px 20px",
                      cursor: "pointer",
                      background: isOpen
                        ? C.subBg
                        : gi % 2 === 0
                          ? "transparent"
                          : C.rowAlt88,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: isOpen ? C.crimson : C.muted,
                          transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                          display: "inline-block",
                        }}
                      >
                        ▶
                      </span>
                      <span
                        style={{ fontSize: 13, fontWeight: 600, color: C.text }}
                      >
                        {grupo}
                      </span>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <span style={{ fontSize: 11, color: C.muted }}>
                        {items.length} lanç.
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: corTotal,
                        }}
                      >
                        R$ {fmt(total * -1)}
                      </span>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ background: C.deepBg }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 12,
                        }}
                      >
                        <thead>
                          <tr style={{ background: C.deepBg2 }}>
                            <th
                              style={{
                                padding: "8px 16px 8px 36px",
                                textAlign: "left",
                                fontSize: 9,
                                color: C.muted,
                              }}
                            >
                              CLIENTE / FORNECEDOR
                            </th>
                            <th
                              style={{
                                padding: "8px 16px",
                                textAlign: "right",
                                fontSize: 9,
                                color: C.muted,
                              }}
                            >
                              VALOR
                            </th>
                            <th
                              style={{
                                padding: "8px 16px",
                                textAlign: "center",
                                fontSize: 9,
                                color: C.muted,
                              }}
                            >
                              DATA
                            </th>
                            <th
                              style={{
                                padding: "8px 16px",
                                textAlign: "center",
                                fontSize: 9,
                                color: C.muted,
                              }}
                            >
                              OBSERVAÇÃO
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((r, i) => {
                            const val =
                              typeof r.Valor === "number"
                                ? r.Valor
                                : parseBRL(r.Valor);
                            return (
                              <tr
                                key={i}
                                style={{ borderTop: `1px solid ${C.border}18` }}
                              >
                                <td
                                  style={{
                                    padding: "8px 16px 8px 36px",
                                    color: C.text,
                                  }}
                                >
                                  {r["Cliente ou Fornecedor"] || "-"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px 16px",
                                    textAlign: "right",
                                    fontWeight: 700,
                                    color: val < 0 ? C.danger : C.success,
                                  }}
                                >
                                  R$ {fmt(val * -1)}
                                </td>
                                <td
                                  style={{
                                    padding: "8px 16px",
                                    textAlign: "center",
                                    color: C.muted,
                                  }}
                                >
                                  {r["Data (completa)"] || "-"}
                                </td>
                                <td
                                  style={{
                                    padding: "8px 16px",
                                    textAlign: "center",
                                    color: C.muted,
                                  }}
                                >
                                  {r["Obs da Conta"] || "-"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
            }}
          >
            <h1 style={{ fontSize: 20, color: "red", fontStyle: "italic" }}>
              * A folha do mês corrente é uma projeção até o fechamento.
            </h1>
          </div>
        </div>

        <div
          style={{
            padding: "14px 24px",
            borderTop: `1px solid ${C.border}`,
            background: C.panelBg,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 11, color: C.muted }}>
            {grupos.length} grupos · {rows.length} lançamentos
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: totalDep < 0 ? C.danger : C.success,
            }}
          >
            Total: R$ {fmt(totalDep * -1)}
          </span>
        </div>
      </div>
    </>
  );
}

// ─── ORÇAMENTO DRAWER (por Grupo) ────────────────────────────────────────────

function OrcamentoDrawer({ dep, orcamento, onClose }) {
  const C = useContext(ThemeContext);
  const [expandedGrupos, setExpandedGrupos] = useState({});

  if (!dep) return null;

  const rows = orcamento.filter((r) => (r.Departamento || "-") === dep);

  // Agrupar por Grupo → CONTA
  const grupoMap = {};
  rows.forEach((r) => {
    const grupo = r.Grupo || "Sem Grupo";
    const conta = r.CONTA || r.Conta || r.conta || "Sem Conta";
    const raw =
      r["Orçamento"] ?? r["Orcamento"] ?? r["orcamento"] ?? r["orçamento"] ?? 0;
    const val = parseBRL(raw) ?? 0;

    if (!grupoMap[grupo]) grupoMap[grupo] = {};
    if (!grupoMap[grupo][conta]) grupoMap[grupo][conta] = 0;
    grupoMap[grupo][conta] += val;
  });

  const grupos = Object.entries(grupoMap)
    .map(([grupo, contasObj]) => {
      const contas = Object.entries(contasObj)
        .map(([conta, total]) => ({ conta, total }))
        .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
      const totalGrupo = contas.reduce((s, c) => s + c.total, 0);
      return { grupo, contas, totalGrupo };
    })
    .sort((a, b) => Math.abs(b.totalGrupo) - Math.abs(a.totalGrupo));

  const totalDep = grupos.reduce((s, g) => s + g.totalGrupo, 0);

  const toggleGrupo = (g) =>
    setExpandedGrupos((prev) => ({ ...prev, [g]: !prev[g] }));
  const expandAll = () =>
    setExpandedGrupos(Object.fromEntries(grupos.map((g) => [g.grupo, true])));
  const collapseAll = () => setExpandedGrupos({});
  const anyOpen = grupos.some((g) => expandedGrupos[g.grupo]);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "#00000088",
          zIndex: 100,
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Painel lateral */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(780px, 96vw)",
          background: C.drawerBg,
          border: `1px solid ${C.border}`,
          borderRight: "none",
          zIndex: 101,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            background: C.panelBg,
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                color: C.muted,
                textTransform: "uppercase",
              }}
            >
              Detalhamento · Orçamento por Grupo
            </p>
            <h2
              style={{
                margin: "4px 0 0",
                fontSize: 16,
                fontWeight: 700,
                color: C.text,
              }}
            >
              {dep}
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: 12, color: C.muted }}>
              {grupos.length} grupo{grupos.length !== 1 ? "s" : ""}
              <span style={{ margin: "0 8px", color: C.border }}>·</span>
              {rows.length} linha{rows.length !== 1 ? "s" : ""} de orçamento
              <span
                style={{ marginLeft: 12, fontWeight: 700, color: C.crimson }}
              >
                Total: R$ {fmt(totalDep)}
              </span>
            </p>
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <button
              onClick={anyOpen ? collapseAll : expandAll}
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.muted,
                fontSize: 11,
                fontWeight: 600,
                padding: "5px 10px",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = C.crimson;
                e.currentTarget.style.color = C.crimson;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = C.border;
                e.currentTarget.style.color = C.muted;
              }}
            >
              {anyOpen ? "− Recolher" : "+ Expandir"}
            </button>
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                color: C.muted,
                fontSize: 18,
                lineHeight: 1,
                width: 34,
                height: 34,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Lista de grupos */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {grupos.length === 0 ? (
            <div
              style={{
                padding: 48,
                textAlign: "center",
                color: C.muted,
                fontSize: 13,
              }}
            >
              Nenhum orçamento encontrado para os filtros ativos.
            </div>
          ) : (
            grupos.map(({ grupo, contas, totalGrupo }, gi) => {
              const isOpen = !!expandedGrupos[grupo];
              const pctTotal =
                totalDep !== 0 ? (totalGrupo / totalDep) * 100 : 0;

              return (
                <div
                  key={grupo}
                  style={{ borderBottom: `1px solid ${C.border}` }}
                >
                  {/* Linha do Grupo */}
                  <div
                    onClick={() => toggleGrupo(grupo)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "13px 20px",
                      cursor: "pointer",
                      background: isOpen
                        ? C.subBg
                        : gi % 2 === 0
                          ? "transparent"
                          : C.rowAlt88,
                      transition: "background 0.12s",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = `${C.crimson}12`)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = isOpen
                        ? C.subBg
                        : gi % 2 === 0
                          ? "transparent"
                          : C.rowAlt88)
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: isOpen ? C.crimson : C.muted,
                          transition: "transform 0.2s, color 0.15s",
                          display: "inline-block",
                          transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                          flexShrink: 0,
                        }}
                      >
                        ▶
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={grupo}
                      >
                        {grupo}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flexShrink: 0,
                        marginLeft: 12,
                      }}
                    >
                      {/* Barra de participação */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            width: 80,
                            height: 4,
                            background: `${C.border}55`,
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(pctTotal, 100)}%`,
                              height: "100%",
                              background: C.crimson,
                              borderRadius: 2,
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: 10,
                            color: C.muted,
                            minWidth: 34,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {pctTotal.toFixed(1)}%
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          color: C.muted,
                          background: C.hover,
                          borderRadius: 12,
                          padding: "2px 8px",
                        }}
                      >
                        {contas.length} conta{contas.length !== 1 ? "s" : ""}
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: C.crimson,
                          fontVariantNumeric: "tabular-nums",
                          minWidth: 100,
                          textAlign: "right",
                        }}
                      >
                        R$ {fmt(totalGrupo)}
                      </span>
                    </div>
                  </div>

                  {/* Sub-tabela de Contas */}
                  {isOpen && (
                    <div
                      style={{
                        background: C.deepBg,
                        borderTop: `1px solid ${C.border}22`,
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 12,
                        }}
                      >
                        <thead>
                          <tr style={{ background: C.deepBg2 }}>
                            <th
                              style={{
                                padding: "8px 16px 8px 36px",
                                textAlign: "left",
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: "0.12em",
                                color: C.muted,
                                textTransform: "uppercase",
                                width: "60%",
                              }}
                            >
                              Conta
                            </th>
                            <th
                              style={{
                                padding: "8px 16px",
                                textAlign: "right",
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: "0.12em",
                                color: C.muted,
                                textTransform: "uppercase",
                                width: "20%",
                              }}
                            >
                              Orçamento
                            </th>
                            <th
                              style={{
                                padding: "8px 16px",
                                textAlign: "right",
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: "0.12em",
                                color: C.muted,
                                textTransform: "uppercase",
                                width: "20%",
                              }}
                            >
                              % do Grupo
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {contas.map(({ conta, total }, ci) => {
                            const pctGrupo =
                              totalGrupo !== 0 ? (total / totalGrupo) * 100 : 0;
                            return (
                              <tr
                                key={ci}
                                style={{
                                  borderTop: `1px solid ${C.border}18`,
                                  background:
                                    ci % 2 === 0 ? "transparent" : C.rowAlt88,
                                  transition: "background 0.1s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.background = `${C.crimson}0D`)
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.background =
                                    ci % 2 === 0 ? "transparent" : C.rowAlt88)
                                }
                              >
                                <td
                                  style={{
                                    padding: "8px 16px 8px 36px",
                                    color: C.text,
                                    fontWeight: 500,
                                  }}
                                >
                                  {conta}
                                </td>
                                <td
                                  style={{
                                    padding: "8px 16px",
                                    textAlign: "right",
                                    fontWeight: 700,
                                    fontVariantNumeric: "tabular-nums",
                                    color: C.crimson,
                                  }}
                                >
                                  R$ {fmt(total)}
                                </td>
                                <td
                                  style={{
                                    padding: "8px 16px",
                                    textAlign: "right",
                                    color: C.muted,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {pctGrupo.toFixed(1)}%
                                </td>
                              </tr>
                            );
                          })}
                          {/* Subtotal do grupo */}
                          <tr
                            style={{
                              borderTop: `1px solid ${C.border}44`,
                              background: C.panelBg,
                            }}
                          >
                            <td
                              style={{
                                padding: "8px 16px 8px 36px",
                                fontSize: 11,
                                fontWeight: 700,
                                color: C.muted,
                              }}
                            >
                              Subtotal
                            </td>
                            <td
                              style={{
                                padding: "8px 16px",
                                textAlign: "right",
                                fontSize: 12,
                                fontWeight: 800,
                                fontVariantNumeric: "tabular-nums",
                                color: C.crimson,
                              }}
                            >
                              R$ {fmt(totalGrupo)}
                            </td>
                            <td
                              style={{
                                padding: "8px 16px",
                                textAlign: "right",
                                fontSize: 11,
                                fontWeight: 700,
                                color: C.muted,
                              }}
                            >
                              {pctTotal.toFixed(1)}% do total
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: `1px solid ${C.border}`,
            background: C.panelBg,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 11, color: C.muted }}>
            {grupos.length} grupo{grupos.length !== 1 ? "s" : ""} ·{" "}
            {rows.length} linha{rows.length !== 1 ? "s" : ""} de orçamento
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.crimson }}>
            Total Orçado: R$ {fmt(totalDep)}
          </span>
        </div>
      </div>
    </>
  );
}

// ─── DETALHE COMPARATIVO DRAWER (Orçado vs Realizado por Grupo) ──────────────

function DetalheComparativoDrawer({
  dep,
  orcFiltrado,
  lancFiltrados,
  onClose,
}) {
  const C = useContext(ThemeContext);

  if (!dep) return null;

  // ── Agregação por Grupo ────────────────────────────────────────────────────
  const orcRows = orcFiltrado.filter((r) => (r.Departamento || "-") === dep);
  const lancRows = lancFiltrados.filter((r) => (r.Departamento || "-") === dep);

  const orcGrupoMap = {};
  orcRows.forEach((r) => {
    const grupo = r.Grupo || "Sem Grupo";
    const val =
      parseBRL(
        r["Orçamento"] ??
          r["Orcamento"] ??
          r["orcamento"] ??
          r["orçamento"] ??
          0,
      ) ?? 0;
    orcGrupoMap[grupo] = (orcGrupoMap[grupo] || 0) + val;
  });

  const realGrupoMap = {};
  lancRows.forEach((r) => {
    const grupo = r.Grupo || "Sem Grupo";
    const val = parseBRL(r.Valor) ?? 0;
    realGrupoMap[grupo] = (realGrupoMap[grupo] || 0) + val;
  });

  const allGrupos = [
    ...new Set([...Object.keys(orcGrupoMap), ...Object.keys(realGrupoMap)]),
  ];

  const rows = allGrupos
    .map((grupo) => {
      const orc = orcGrupoMap[grupo] ?? null;
      const rawReal = realGrupoMap[grupo] ?? null;
      const absReal = rawReal != null ? Math.abs(rawReal) : null;
      const pct =
        orc != null && orc !== 0 && absReal != null
          ? (absReal / orc) * 100
          : null;
      const delta = orc != null && absReal != null ? orc - absReal : null;
      return { grupo, orc, absReal, pct, delta };
    })
    .sort((a, b) => (b.orc || 0) - (a.orc || 0));

  const totalOrc = rows.reduce((s, r) => s + (r.orc || 0), 0);
  const totalReal = rows.reduce((s, r) => s + (r.absReal || 0), 0);
  const totalPct = totalOrc !== 0 ? (totalReal / totalOrc) * 100 : null;
  const totalDelta = totalOrc - totalReal;

  const pctColor = (pct) => {
    if (pct == null) return C.muted;
    if (pct >= 100) return "#FF2D55";
    if (pct >= 80) return C.amber;
    if (pct >= 60) return "#F0C040";
    return C.success;
  };
  const pctBg = (pct) => {
    if (pct == null) return "transparent";
    if (pct >= 100) return "#FF2D5515";
    if (pct >= 80) return `${C.amber}18`;
    if (pct >= 60) return "#F0C04015";
    return `${C.success}12`;
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "#00000088",
          zIndex: 100,
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Painel lateral */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(860px, 96vw)",
          background: C.drawerBg,
          border: `1px solid ${C.border}`,
          borderRight: "none",
          zIndex: 101,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${C.border}`,
            background: C.panelBg,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                color: C.muted,
                textTransform: "uppercase",
              }}
            >
              Detalhamento · Orçado vs Realizado por Grupo
            </p>
            <h2
              style={{
                margin: "4px 0 0",
                fontSize: 16,
                fontWeight: 700,
                color: C.text,
              }}
            >
              {dep}
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: 12, color: C.muted }}>
              {rows.length} grupo{rows.length !== 1 ? "s" : ""}
              <span style={{ margin: "0 8px", color: C.border }}>·</span>
              <span style={{ fontWeight: 700, color: C.crimson }}>
                Orçado: R$ {fmt(totalOrc)}
              </span>
              <span style={{ margin: "0 8px", color: C.border }}>·</span>
              <span
                style={{
                  fontWeight: 700,
                  color:
                    totalPct != null && totalPct >= 100 ? "#FF2D55" : C.success,
                }}
              >
                Realizado: R$ {fmt(totalReal)}
              </span>
              {totalPct != null && (
                <>
                  <span style={{ margin: "0 8px", color: C.border }}>·</span>
                  <span style={{ fontWeight: 800, color: pctColor(totalPct) }}>
                    {totalPct.toFixed(1)}%
                  </span>
                </>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.muted,
              fontSize: 18,
              lineHeight: 1,
              width: 34,
              height: 34,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = C.crimson;
              e.currentTarget.style.color = C.crimson;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.color = C.muted;
            }}
          >
            ×
          </button>
        </div>

        {/* Tabela comparativa */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {rows.length === 0 ? (
            <div
              style={{
                padding: 48,
                textAlign: "center",
                color: C.muted,
                fontSize: 13,
              }}
            >
              Nenhum dado encontrado para os filtros ativos.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr
                  style={{
                    background: C.panelBg,
                    borderBottom: `2px solid ${C.border}`,
                  }}
                >
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      color: C.muted,
                      textTransform: "uppercase",
                    }}
                  >
                    Grupo
                  </th>
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "right",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      color: C.muted,
                      textTransform: "uppercase",
                    }}
                  >
                    Orçado
                  </th>
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "right",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      color: C.muted,
                      textTransform: "uppercase",
                    }}
                  >
                    Realizado
                  </th>
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "right",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      color: C.muted,
                      textTransform: "uppercase",
                    }}
                  >
                    Delta
                  </th>
                  <th
                    style={{
                      padding: "12px 20px",
                      textAlign: "center",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      color: C.muted,
                      textTransform: "uppercase",
                      minWidth: 100,
                    }}
                  >
                    % Realizado
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ grupo, orc, absReal, pct, delta }, i) => (
                  <tr
                    key={grupo}
                    style={{
                      borderBottom: `1px solid ${C.border}`,
                      background: i % 2 === 0 ? "transparent" : C.rowAlt88,
                      transition: "background 0.12s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = `${C.crimson}12`)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        i % 2 === 0 ? "transparent" : C.rowAlt88)
                    }
                  >
                    {/* Grupo */}
                    <td
                      style={{
                        padding: "12px 20px",
                        fontWeight: 600,
                        color: C.text,
                      }}
                    >
                      {grupo}
                    </td>

                    {/* Orçado */}
                    <td
                      style={{
                        padding: "12px 20px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: orc != null ? C.crimson : C.muted,
                      }}
                    >
                      {orc != null ? `R$ ${fmt(orc)}` : "—"}
                    </td>

                    {/* Realizado */}
                    <td
                      style={{
                        padding: "12px 20px",
                        textAlign: "right",
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color:
                          absReal != null
                            ? pct != null && pct >= 100
                              ? "#FF2D55"
                              : C.success
                            : C.muted,
                      }}
                    >
                      {absReal != null ? `R$ ${fmt(absReal)}` : "—"}
                    </td>

                    {/* Delta */}
                    <td
                      style={{
                        padding: "12px 20px",
                        textAlign: "right",
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color:
                          delta == null
                            ? C.muted
                            : delta < 0
                              ? "#FF2D55"
                              : C.success,
                      }}
                    >
                      {delta != null ? `R$ ${fmt(delta)}` : "—"}
                    </td>

                    {/* % Realizado */}
                    <td style={{ padding: "12px 20px", textAlign: "center" }}>
                      {pct != null ? (
                        <span
                          style={{
                            display: "inline-block",
                            minWidth: 64,
                            padding: "3px 10px",
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 700,
                            color: pctColor(pct),
                            background: pctBg(pct),
                            border: `1px solid ${pctColor(pct)}44`,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {pct.toFixed(1)}%
                        </span>
                      ) : (
                        <span style={{ color: C.muted }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}

                {/* Linha de Total */}
                <tr
                  style={{
                    borderTop: `2px solid ${C.border}`,
                    background: C.panelBg,
                  }}
                >
                  <td
                    style={{
                      padding: "13px 20px",
                      fontWeight: 800,
                      color: C.text,
                    }}
                  >
                    Total
                  </td>
                  <td
                    style={{
                      padding: "13px 20px",
                      textAlign: "right",
                      fontWeight: 800,
                      fontVariantNumeric: "tabular-nums",
                      color: C.crimson,
                    }}
                  >
                    R$ {fmt(totalOrc)}
                  </td>
                  <td
                    style={{
                      padding: "13px 20px",
                      textAlign: "right",
                      fontWeight: 800,
                      fontVariantNumeric: "tabular-nums",
                      color:
                        totalPct != null && totalPct >= 100
                          ? "#FF2D55"
                          : C.success,
                    }}
                  >
                    R$ {fmt(totalReal)}
                  </td>
                  <td
                    style={{
                      padding: "13px 20px",
                      textAlign: "right",
                      fontWeight: 800,
                      fontVariantNumeric: "tabular-nums",
                      color: totalDelta < 0 ? "#FF2D55" : C.success,
                    }}
                  >
                    R$ {fmt(totalDelta)}
                  </td>
                  <td style={{ padding: "13px 20px", textAlign: "center" }}>
                    {totalPct != null ? (
                      <span
                        style={{
                          display: "inline-block",
                          minWidth: 64,
                          padding: "3px 10px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 800,
                          color: pctColor(totalPct),
                          background: pctBg(totalPct),
                          border: `1px solid ${pctColor(totalPct)}44`,
                        }}
                      >
                        {totalPct.toFixed(1)}%
                      </span>
                    ) : (
                      <span style={{ color: C.muted }}>—</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 24px",
            borderTop: `1px solid ${C.border}`,
            background: C.panelBg,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ fontSize: 10, color: C.muted, fontStyle: "italic" }}>
            Realizado capturado dos lançamentos filtrados · Valores negativos
            tratados como despesa
          </span>
        </div>
      </div>
    </>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function FinanceiroDashboard() {
  const [isLight, setisLight] = useState(true);

  const C = isLight ? LIGHT_THEME_ORCAMENTO : DARK_THEME_ORCAMENTO;
  /* Cores recharts */
  const CPREV = C.crimson;
  const CREAL = C.amber;
  const CGRID = isLight ? "#E5E0F0" : "#2E2050";

  const CTXT = C.muted;
  const axisStyle = { fontSize: 11, fill: CTXT };
  const tooltipStyle = {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    fontSize: 12,
  };

  const ChartLoader = ({ color = "#9585C0", height = 240 }) => (
    <div
      style={{
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <RefreshCw
        size={28}
        color={color}
        style={{ animation: "spin 1s linear infinite" }}
      />
    </div>
  );

  function ChartCard({ title, subtitle, children, style, loading }) {
    return (
      <div
        style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          padding: "16px 16px 12px",
          boxShadow: isLight
            ? "0 1px 4px rgba(0,0,0,0.06)"
            : "0 1px 4px rgba(0,0,0,0.3)",
          ...style,
        }}
      >
        <p
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: C.text,
            margin: "0 0 1px",
          }}
        >
          {title}
        </p>

        {subtitle && (
          <p
            style={{
              fontSize: 11,
              color: C.muted,
              margin: "0 0 12px",
            }}
          >
            {subtitle}
          </p>
        )}

        <div style={{ marginTop: 10 }}>
          {loading ? <ChartLoader color={C.muted} /> : children}
        </div>
      </div>
    );
  }

  const [lancamentos, setLancamentos] = useState([]);
  const [lancamentosData, setLancamentosData] = useState("");

  const [despesas, setDespesas] = useState([]);
  const [orcamento, setOrcamento] = useState([]);
  const [realizadoMaoDeObra, setRealizadoMaoDeObra] = useState([]);

  const [status, setStatus] = useState({ loading: false, error: null });
  const [drawerDep, setDrawerDep] = useState(null);
  const [drawerOrcDep, setDrawerOrcDep] = useState(null);
  const [drawerDetalheDep, setDrawerDetalheDep] = useState(null);

  const roleAtual = getRoleFromToken();
  const roleUpper = roleAtual?.toUpperCase?.() ?? "";

  // Detecta se é um coordenador (ex: "COORDENADOR_RH", "COORDENADOR_TI") e
  // resolve o departamento reaproveitando DEPARTAMENTO_POR_ROLE pelo sufixo.
  const isCoord = roleUpper.includes("COORDENADOR");
  const coordSufixo = roleUpper.replace("COORDENADOR_", "");
  const coordDep = DEPARTAMENTO_POR_ROLE[coordSufixo] ?? [coordSufixo];

  // Escopos de permissão da role atual — null significa sem restrição
  // (ex: ADMIN). Ver definição de ROLE_SCOPES no topo do arquivo.
  const escopos = isCoord
    ? [{ departamentos: coordDep }]
    : (ROLE_SCOPES[roleUpper] ?? null);

  // Restrição de folha: qualquer COORDENADOR_* (ou role listada em
  // ROLE_REMOVER_FOLHA) fica sem os grupos de salários/benefícios.
  const catsFolhaRemover = isCoord
    ? ROLE_REMOVER_FOLHA["COORDENADOR"] || []
    : ROLE_REMOVER_FOLHA[roleUpper] || [];

  // Todos os filtros nascem em "all": o efeito de reconciliação logo abaixo
  // (após `opcoesDisponiveis` ser definido) os estreita automaticamente para
  // o que a role realmente pode ver assim que os dados chegam — não há mais
  // necessidade de pré-calcular um valor inicial "travado" por role aqui.
  const [filtCatSuperiorLanc, setFiltCatSuperiorLanc] = useState(["all"]);
  const [filtCatInferiorLanc, setFiltCatInferiorLanc] = useState(["all"]);
  const [filtMes, setFiltMes] = useState(["all"]);
  const [filtDep, setFiltDep] = useState(["all"]);

  const navigate = useNavigate();

  const fetchDados = async () => {
    setStatus({ loading: true, error: null });
    try {
      const [jL, jO, jR] = await Promise.all([
        getDadosLancamentosDash(),
        getDadosOrcamento(),
        getLancamentosRealizado(),
      ]);

      if (!jL.success) throw new Error(`Lançamentos: ${jL.error}`);
      if (!jO.success) throw new Error(`Orçamento: ${jO.error}`);
      if (!jR.success) throw new Error(`Realizado Mão de Obra: ${jR.error}`);

      // 1. Array original de Lançamentos Dash
      const lancamentosDash = jL.data || [];
      const lacamentoData = lancamentosDash[0]?.["Data Atualização"] || "";
      // 2. Normalizar os dados de Mão de Obra para a mesma estrutura
      const maoDeObraNormalizada = (jR.data || []).map((item) => {
        return {
          Categoria: item.CONTA,
          "Data (completa)": item.Periodo,
          Departamento: item["Departamento Oficial"],
          Grupo: item.Grupo,

          // Multiplicando por -1 para seguir a mesma lógica de valores negativos (despesas) do Dash
          Valor: Number(item.Realizado) * -1,

          // Preenchendo campos ausentes para evitar erros na renderização do Drawer
          "Cliente ou Fornecedor": "Mão de Obra",
          "Conta Corrente": "N/D",
          "Minha Empresa (Razão Social)": "N/D",
          "Obs da Conta": "Lançamento de Mão de Obra Realizada",
          Projeto: "N/D",
        };
      });

      // 3. Mesclar os dois arrays
      const todosLancamentos = [...lancamentosDash, ...maoDeObraNormalizada];

      // 4. Salvar nos estados
      setLancamentos(todosLancamentos);
      setLancamentosData(lacamentoData);
      setOrcamento(jO.data || []);
      setRealizadoMaoDeObra(maoDeObraNormalizada);

      setStatus({ loading: false, error: null });
    } catch (err) {
      setStatus({ loading: false, error: err.message });
    }
  };

  useEffect(() => {
    fetchDados();
  }, []);
  // ─── MOTOR ÚNICO DE PERMISSÕES + FILTROS DEPENDENTES ─────────────────────
  // Visão normalizada de lançamentos + orçamento (mesmos nomes de campo para
  // os dois datasets), usada tanto para calcular as opções de cada dropdown
  // quanto para o filtro final dos dados exibidos.
  const universo = useMemo(() => {
    const deLanc = lancamentos.map((r) => ({
      grupo: r.Grupo,
      categoria: r.Categoria,
      departamento: r.Departamento || "-",
      mes: toMonthKey(r["Data (completa)"]),
    }));
    const deOrc = orcamento.map((r) => ({
      grupo: r.Grupo,
      categoria: r.CONTA,
      departamento: r.Departamento || "-",
      mes: toMonthKey(r.Periodo),
    }));
    return [...deLanc, ...deOrc];
  }, [lancamentos, orcamento]);

  // Um registro casa com um escopo se satisfizer TODAS as dimensões
  // declaradas nele. Dimensão omitida no escopo = liberada nele.
  const casaComEscopo = (escopo, row) =>
    (!escopo.grupos || escopo.grupos.includes(row.grupo)) &&
    (!escopo.categorias || escopo.categorias.includes(row.categoria)) &&
    (!escopo.departamentos || escopo.departamentos.includes(row.departamento));

  // Permissão da role: sem restrição (escopos null, ex: ADMIN) ou casa com
  // QUALQUER escopo da role (OR entre escopos) — e não é um grupo removido
  // pela restrição de folha (coordenadores).
  const passaPermissoes = (row) =>
    (!escopos || escopos.some((e) => casaComEscopo(e, row))) &&
    !catsFolhaRemover.includes(row.grupo);

  // Casa um registro com os filtros SELECIONADOS atualmente, ignorando as
  // dimensões presentes em `ignoradas` — usada para calcular as opções
  // válidas de cada dropdown com base nos demais filtros já escolhidos.
  const passaFiltrosAtuais = (row, ignoradas) =>
    (ignoradas.has("grupo") ||
      filtCatSuperiorLanc.includes("all") ||
      filtCatSuperiorLanc.includes(row.grupo)) &&
    (ignoradas.has("categoria") ||
      filtCatInferiorLanc.includes("all") ||
      filtCatInferiorLanc.includes(row.categoria)) &&
    (ignoradas.has("departamento") ||
      filtDep.includes("all") ||
      filtDep.includes(row.departamento)) &&
    (ignoradas.has("mes") || isMonthInFilters(row.mes, filtMes));

  // Função única responsável por calcular as opções disponíveis de uma
  // dimensão: valores existentes nos dados que a role tem permissão de ver E
  // que são compatíveis com os filtros indicados em `considerar`.
  //
  // A dependência entre os 3 dropdowns é hierárquica e unidirecional —
  // Grupo → Categoria → Departamento (Mês influencia os três, mas não é
  // influenciado por nenhum) — de propósito: se Departamento também
  // influenciasse as opções de Grupo, uma seleção que trava o Departamento
  // (ex: só "Financeiro" válido) travaria de volta o próprio Grupo num ciclo,
  // e o usuário nunca mais conseguiria voltar para outro Grupo.
  const opcoesDisponiveis = (dimensao, considerar) => {
    const ignoradas = new Set(
      ["grupo", "categoria", "departamento"].filter((d) => !considerar.includes(d)),
    );
    const valores = new Set();
    universo.forEach((row) => {
      if (!passaPermissoes(row)) return;
      if (!passaFiltrosAtuais(row, ignoradas)) return;
      if (row[dimensao]) valores.add(row[dimensao]);
    });
    return [...valores].sort();
  };

  // Grupo: só depende da permissão da role e do mês.
  const catSuperiorLancOpcoesRaw = useMemo(
    () => opcoesDisponiveis("grupo", []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [universo, escopos, catsFolhaRemover, filtMes],
  );
  // Categoria: depende do Grupo selecionado (e do mês).
  const catInferiorLancOpcoesRaw = useMemo(
    () => opcoesDisponiveis("categoria", ["grupo"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [universo, escopos, catsFolhaRemover, filtCatSuperiorLanc, filtMes],
  );
  // Departamento: depende do Grupo e da Categoria selecionados (e do mês).
  const depOpcoesRaw = useMemo(
    () => opcoesDisponiveis("departamento", ["grupo", "categoria"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [universo, escopos, catsFolhaRemover, filtCatSuperiorLanc, filtCatInferiorLanc, filtMes],
  );

  // Reconcilia cada filtro com as opções que passaram a ser válidas: se a
  // dimensão só tem uma opção possível dado o resto da seleção, trava nela;
  // se a seleção atual saiu do conjunto válido, volta para "all". Mantém os
  // filtros sempre sincronizados entre si — para qualquer role, Admin incluso.
  const reconciliar = (valorAtual, opcoesRaw, setter) => {
    const atual = Array.isArray(valorAtual) ? valorAtual : [valorAtual];
    if (opcoesRaw.length <= 1) {
      const alvo = opcoesRaw.length === 1 ? opcoesRaw : ["all"];
      if (JSON.stringify(atual) !== JSON.stringify(alvo)) setter(alvo);
      return;
    }
    if (atual.includes("all")) return;
    const validos = atual.filter((v) => opcoesRaw.includes(v));
    if (validos.length === 0) setter(["all"]);
    else if (validos.length !== atual.length) setter(validos);
  };

  useEffect(() => {
    reconciliar(filtDep, depOpcoesRaw, setFiltDep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depOpcoesRaw]);

  useEffect(() => {
    reconciliar(filtCatSuperiorLanc, catSuperiorLancOpcoesRaw, setFiltCatSuperiorLanc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catSuperiorLancOpcoesRaw]);

  useEffect(() => {
    reconciliar(filtCatInferiorLanc, catInferiorLancOpcoesRaw, setFiltCatInferiorLanc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catInferiorLancOpcoesRaw]);

  // --- FILTRO DE LANÇAMENTOS (REALIZADO) ---
  const lancFiltrados = useMemo(
    () =>
      lancamentos.filter((r) => {
        const row = {
          grupo: r.Grupo,
          categoria: r.Categoria,
          departamento: r.Departamento || "-",
          mes: toMonthKey(r["Data (completa)"]),
        };
        return passaPermissoes(row) && passaFiltrosAtuais(row, new Set());
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      lancamentos,
      escopos,
      catsFolhaRemover,
      filtCatSuperiorLanc,
      filtCatInferiorLanc,
      filtDep,
      filtMes,
    ],
  );

  const orcFiltrado = useMemo(
    () =>
      orcamento.filter((r) => {
        const row = {
          grupo: r.Grupo,
          categoria: r.CONTA,
          departamento: r.Departamento || "-",
          mes: toMonthKey(r.Periodo),
        };
        return passaPermissoes(row) && passaFiltrosAtuais(row, new Set());
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orcamento,
      escopos,
      catsFolhaRemover,
      filtCatSuperiorLanc,
      filtCatInferiorLanc,
      filtDep,
      filtMes,
    ],
  );
  const orcadoRealizadoPorMes = useMemo(() => {
    const mapa = {};

    // Agrega realizado por mês — USANDO DADOS FILTRADOS (lancFiltrados)
    lancFiltrados.forEach((item) => {
      const data = item["Data (completa)"];
      if (!data || item["Valor"] === undefined) return;
      const [, mes, ano] = data.split("/");
      const key = `${mes}/${ano}`;
      if (!mapa[key]) mapa[key] = { mes: key, realizado: 0, orcado: 0 };
      mapa[key].realizado += Math.abs(Number(item["Valor"]));
    });

    // Agrega orçado por mês — USANDO DADOS FILTRADOS (orcFiltrado)
    orcFiltrado.forEach((item) => {
      const periodo = item["Periodo"];
      if (!periodo || item["Orçamento"] === undefined) return;
      const [, mes, ano] = periodo.split("/");
      const key = `${mes}/${ano}`;
      if (!mapa[key]) mapa[key] = { mes: key, realizado: 0, orcado: 0 };
      mapa[key].orcado += Number(item["Orçamento"]);
    });

    return Object.values(mapa).sort((a, b) => {
      const [ma, ya] = a.mes.split("/").map(Number);
      const [mb, yb] = b.mes.split("/").map(Number);
      return ya !== yb ? ya - yb : ma - mb;
    });
  }, [lancFiltrados, orcFiltrado]);

  // Opções exibidas nos três dropdowns dependentes — apenas empacotam em
  // {value,label} o que `opcoesDisponiveis` (acima) já calculou levando em
  // conta permissão da role + demais filtros selecionados.
  const opcoesFiltradas = useMemo(
    () => [
      { value: "all", label: "Categorias" },
      ...catInferiorLancOpcoesRaw.map((c) => ({ value: c, label: c })),
    ],
    [catInferiorLancOpcoesRaw],
  );

  const catSuperiorLancOpcoes = useMemo(
    () => [
      { value: "all", label: "Categorias Superiores" },
      ...catSuperiorLancOpcoesRaw.map((g) => ({ value: g, label: g })),
    ],
    [catSuperiorLancOpcoesRaw],
  );

  const depOpcoes = useMemo(
    () => [
      { value: "all", label: "Todos os Departamentos" },
      ...depOpcoesRaw.map((d) => ({ value: d, label: d })),
    ],
    [depOpcoesRaw],
  );

  const mesOpcoes = useMemo(() => {
    // 1. Mapeia as chaves de meses existentes nos dados
    const keysLanc = lancamentos
      .map((r) => toMonthKey(r["Data (completa)"]))
      .filter(Boolean);
    const keysOrc = orcamento.map((r) => toMonthKey(r.Periodo)).filter(Boolean);

    // 2. Tenta inferir o ano para meses que venham sem ele (ex: vindo de despesas)
    const anosPorMes = {};
    keysLanc.forEach((k) => {
      const [mm, yyyy] = k.split("/");
      if (mm && yyyy) anosPorMes[mm] = yyyy;
    });
    keysOrc.forEach((k) => {
      const [mm, yyyy] = k.split("/");
      if (mm && yyyy) anosPorMes[mm] = yyyy;
    });

    const keysDesp = despesas
      .map((r) => {
        const base = toMonthKey(r.Periodo);
        if (!base) return null;
        if (base.includes("/")) return base;
        const ano = anosPorMes[base];
        return ano ? `${base}/${ano}` : null;
      })
      .filter(Boolean);

    // 3. Gera a lista de meses únicos e ordena cronologicamente
    const allKeys = [...new Set([...keysLanc, ...keysOrc, ...keysDesp])]
      .filter((k) => k.includes("/"))
      .sort((a, b) => {
        const [ma, ya] = a.split("/").map(Number);
        const [mb, yb] = b.split("/").map(Number);
        return ya !== yb ? ya - yb : ma - mb;
      });

    // 4. Retorna o array final com "Todos", "Trimestres" e "Meses Individuais"
    return [
      { value: "all", label: "Todos os Meses" },
      // --- ADIÇÃO DOS TRIMESTRES ---
      { value: "Q1", label: "1º Trimestre (Jan-Mar)" },
      { value: "Q2", label: "2º Trimestre (Abr-Jun)" },
      { value: "Q3", label: "3º Trimestre (Jul-Set)" },
      { value: "Q4", label: "4º Trimestre (Out-Dez)" },
      // -----------------------------
      ...allKeys.map((k) => ({ value: k, label: monthLabel(k) })),
    ];
  }, [lancamentos, orcamento, despesas]);

  const despFiltradas = useMemo(
    () =>
      despesas.filter((r) => {
        if (filtMes.includes("all")) return true;
        const keyDesp = toMonthKey(r.Periodo);
        const mmFiltro = monthNumFromKey(filtMes);
        const mmDesp = monthNumFromKey(keyDesp);
        return keyDesp && keyDesp.includes("/")
          ? keyDesp === filtMes
          : mmDesp === mmFiltro;
      }),
    [despesas, filtMes],
  );

  const merged = useMemo(() => {
    const realizadoMap = {};
    lancFiltrados.forEach((r) => {
      const dep = r.Departamento || "-";
      const val = parseBRL(r.Valor);
      if (val != null) realizadoMap[dep] = (realizadoMap[dep] || 0) + val;
    });

    const orcMap = {};
    orcFiltrado.forEach((r) => {
      const dep = r.Departamento || "-";
      const raw =
        r["Orçamento"] ?? r["Orcamento"] ?? r["orcamento"] ?? r["orçamento"];
      const val = parseBRL(raw);
      if (val != null) orcMap[dep] = (orcMap[dep] || 0) + val;
    });

    const aprovadoMap = {};
    despFiltradas.forEach((r) => {
      const dep = r.Departamento || "-";
      const val = parseBRL(r.Valor);
      if (val != null) aprovadoMap[dep] = (aprovadoMap[dep] || 0) + val;
    });

    const deps = [
      ...new Set([
        ...Object.keys(realizadoMap),
        ...Object.keys(orcMap),
        ...Object.keys(aprovadoMap),
      ]),
    ].sort();

    return deps.map((dep) => ({
      Departamento: dep,
      Realizado: realizadoMap[dep] ?? null,
      Orçamento: orcMap[dep] ?? null,
      Aprovado: aprovadoMap[dep] ?? null,
    }));
  }, [lancFiltrados, orcFiltrado, despFiltradas]);

  const kpis = useMemo(() => {
    const totalOrc = merged.reduce((s, r) => s + (r.Orçamento || 0), 0);
    const totalReal = merged.reduce((s, r) => s + (r.Realizado || 0), 0);
    const totalAprovado = merged.reduce((s, r) => s + (r.Aprovado || 0), 0);
    return {
      totalOrc,
      totalReal,
      totalAprovado,
      deltaReal: totalOrc + totalReal,
      deltaRealAprov: totalOrc + totalReal - totalAprovado,
    };
  }, [merged]);

  const chartData = useMemo(
    () =>
      merged
        .filter(
          (r) =>
            r.Departamento !== "-" &&
            (r.Orçamento || r.Realizado || r.Aprovado),
        )
        .map((r) => ({
          name: r.Departamento.replace(/DIRETORIA\s+/i, "DIR. ").substring(
            0,
            22,
          ),
          Orçamento: r.Orçamento,
          Realizado: r.Realizado != null ? Math.abs(r.Realizado) : null,
          Aprovado: r.Aprovado,
        })),
    [merged],
  );

  // ── Eficiência: Realizado / Orçado (baseado nos dados filtrados) ──
  const eficiencia = useMemo(() => {
    const totalOrc = kpis.totalOrc;
    const totalReal = Math.abs(kpis.totalReal);
    if (!totalOrc || totalOrc === 0) return null;
    return (totalReal / totalOrc) * 100;
  }, [kpis]);

  // ── Gastos por Grupo: Realizado (lançamentos) x Orçado ──
  const gastosPorGrupo = useMemo(() => {
    const grupoReal = {};
    lancFiltrados.forEach((r) => {
      const grupo = r.Grupo || "Sem Grupo";
      const val = parseBRL(r.Valor);
      if (val != null)
        grupoReal[grupo] = (grupoReal[grupo] || 0) + Math.abs(val);
    });

    const grupoOrc = {};
    orcFiltrado.forEach((r) => {
      const grupo = r.Grupo || "Sem Grupo";
      const raw =
        r["Orçamento"] ?? r["Orcamento"] ?? r["orcamento"] ?? r["orçamento"];
      const val = parseBRL(raw);
      if (val != null) grupoOrc[grupo] = (grupoOrc[grupo] || 0) + val;
    });

    const grupos = [
      ...new Set([...Object.keys(grupoReal), ...Object.keys(grupoOrc)]),
    ];

    return grupos
      .map((grupo) => ({
        name: grupo.length > 30 ? grupo.substring(0, 28) + "…" : grupo,
        Realizado: grupoReal[grupo] ?? 0,
        Orçado: grupoOrc[grupo] ?? 0,
      }))
      .filter((g) => g.Realizado > 0 || g.Orçado > 0)
      .sort((a, b) => b.Realizado - a.Realizado);
  }, [lancFiltrados, orcFiltrado]);

  const [exportando, setExportando] = useState(false);
  const handleExport = async (formato) => {
    const nomes = (val, opcoes) => {
      const arr = Array.isArray(val) ? val : [val];
      return arr.includes("all")
        ? []
        : arr.map((v) => opcoes.find((o) => o.value === v)?.label || v);
    };
    setExportando(true);
    try {
      // Soma realizado e orçado da mesma Grupo/Categoria/Departamento, independente da data.
      const mapa = {};
      const linha = (grupo, categoria, departamento) => {
        const k = `${grupo}|${categoria}|${departamento}`;
        if (!mapa[k])
          mapa[k] = { grupo, categoria, departamento, qtd: 0, valor: 0, orcado: 0 };
        return mapa[k];
      };
      lancFiltrados.forEach((r) => {
        const l = linha(
          r.Grupo || "Sem Grupo",
          r.Categoria || "Sem Categoria",
          r.Departamento || "-",
        );
        l.qtd += 1;
        l.valor += Math.abs(parseBRL(r.Valor) || 0);
      });
      orcFiltrado.forEach((r) => {
        const l = linha(
          r.Grupo || "Sem Grupo",
          r.CONTA || "Sem Categoria",
          r.Departamento || "-",
        );
        l.orcado += parseBRL(r["Orçamento"] ?? r["Orcamento"]) || 0;
      });
      const detalhe = Object.values(mapa)
        .filter((d) => d.valor || d.orcado)
        .map((d) => ({ ...d, diferenca: d.orcado - d.valor }))
        .sort(
        (a, b) =>
          a.grupo.localeCompare(b.grupo) ||
          a.categoria.localeCompare(b.categoria) ||
          a.departamento.localeCompare(b.departamento),
      );
      const payload = {
        filtros: {
          categorias: nomes(filtCatInferiorLanc, opcoesFiltradas),
          grupos: nomes(filtCatSuperiorLanc, catSuperiorLancOpcoes),
          departamento: nomes(filtDep, depOpcoes),
          meses: nomes(filtMes, mesOpcoes),
        },
        totalOrcado: kpis.totalOrc,
        totalRealizado: Math.abs(kpis.totalReal),
        detalhe,
        fmt,
      };
      if (formato === "xlsx") await exportOrcamentoXlsx(payload);
      else await exportOrcamentoDocx(payload);
    } catch (err) {
      alert(`Erro ao exportar: ${err.message}`);
    } finally {
      setExportando(false);
    }
  };

  const isInitialLoad = status.loading && lancamentos.length === 0;

  return (
    <ThemeContext.Provider value={C}>
      <div
        style={{
          background: C.darkBg,
          minHeight: "100vh",
          fontFamily: "'DM Sans','Segoe UI',sans-serif",
          color: C.text,
          transition: "background 0.25s, color 0.25s",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <SideBarNavOrcamento isLight={isLight} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Drawer de detalhamento por departamento */}
        {drawerDep && (
          <DepartmentDrawer
            dep={drawerDep}
            lancamentos={lancFiltrados}
            onClose={() => setDrawerDep(null)}
          />
        )}

        {/* Drawer de detalhamento de orçamento por grupo */}
        {drawerOrcDep && (
          <OrcamentoDrawer
            dep={drawerOrcDep}
            orcamento={orcFiltrado}
            onClose={() => setDrawerOrcDep(null)}
          />
        )}

        {/* Drawer comparativo Orçado vs Realizado por Grupo */}
        {drawerDetalheDep && (
          <DetalheComparativoDrawer
            dep={drawerDetalheDep}
            orcFiltrado={orcFiltrado}
            lancFiltrados={lancFiltrados}
            onClose={() => setDrawerDetalheDep(null)}
          />
        )}

        <header
          style={{ background: C.headerGradient }}
          className="text-white px-4 py-4 lg:px-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4"
        >
          {/* 1. Logo e Infos - Sempre no topo em mobile */}
          <div className="flex items-center justify-between w-full lg:w-auto">
            <div className="flex items-center space-x-2">
              <div>
                <span className="text-2xl font-bold">YOUX</span>
                <span className="text-xl font-light">GROUP</span>
                <h1
                  style={{ color: C.headerText }}
                  className="text-[10px] uppercase tracking-wider"
                >
                  {lancamentosData ? `Atualizado em ${lancamentosData}` : "Sem data de atualização"}
                </h1>
              </div>
            </div>

            {/* Toggle e Menu aparecem aqui no mobile para economizar espaço */}
            <div className="flex items-center gap-2 lg:hidden">
              <ThemeToggle
                isLight={isLight}
                onToggle={() => setisLight((d) => !d)}
              />
              <button
                onClick={() => navigate("/inicio")}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <span className="font-bold text-xs">Menu</span>
              </button>
            </div>
          </div>

          {/* 3. Ações de Desktop - Escondidas no mobile (já que estão no topo) */}
          <div className="hidden lg:flex items-center gap-4">
            <ThemeToggle
              isLight={isLight}
              onToggle={() => setisLight((d) => !d)}
            />
            <button
              onClick={() => navigate("/inicio")}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold whitespace-nowrap"
            >
              Menu Principal
            </button>
          </div>
        </header>
        <div style={{ padding: "32px 40px", maxWidth: 1400, margin: "0 auto" }}>
          {/* ══════ FILTROS ══════ */}
          <div
            style={{
              padding: "32px 40px",
              maxWidth: 1400,
              margin: "0 auto",
            }}
          >
            <div
              style={{ maxWidth: 2000, margin: "0 auto" }}
            >
              {/* ══════ FILTROS ══════ */}
              <div
                style={{
                  background: C.cardBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: 16,
                  marginBottom: 20,
                  boxShadow: isLight
                    ? "0 1px 4px rgba(0,0,0,0.06)"
                    : "0 1px 4px rgba(0,0,0,0.4)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <Filter size={14} color={C.muted} />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: "bold",
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Filtros
                  </span>
                  <button
                    onClick={() => handleExport("xlsx")}
                    disabled={exportando || status.loading}
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.text,
                      background: "transparent",
                      border: `1px solid ${C.border}`,
                      borderRadius: 6,
                      cursor: exportando ? "wait" : "pointer",
                      opacity: exportando || status.loading ? 0.6 : 1,
                    }}
                  >
                    <FileDown size={14} />
                    {exportando ? "Exportando..." : "Exportar Excel"}
                  </button>
                  <button
                    onClick={() => handleExport("docx")}
                    disabled={exportando || status.loading}
                    style={{
                      
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.text,
                      background: "transparent",
                      border: `1px solid ${C.border}`,
                      borderRadius: 6,
                      cursor: exportando ? "wait" : "pointer",
                      opacity: exportando || status.loading ? 0.6 : 1,
                    }}
                  >
                    <FileDown size={14} />
                    {exportando ? "Exportando..." : "Exportar DOCX"}
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 12,
                    alignItems: "end",
                  }}
                >
                  <div>
                    <label
                      style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}
                    >
                      Categorias
                    </label>
                    <MultiSelect
                      options={opcoesFiltradas} // Agora exibe apenas o que ele pode ver
                      value={filtCatInferiorLanc}
                      onChange={setFiltCatInferiorLanc}
                      disabled={false} // Deixe habilitado para ele poder escolher entre as permitidas
                      placeholder="Selecionar..."
                    />
                  </div>

                  <div>
                    <label
                      style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}
                    >
                      Grupos
                    </label>
                    <MultiSelect
                      options={catSuperiorLancOpcoes}
                      value={filtCatSuperiorLanc}
                      onChange={(val) => {
                        setFiltCatSuperiorLanc(val);
                        setFiltCatInferiorLanc(["all"]);
                      }}
                      disabled={catSuperiorLancOpcoesRaw.length <= 1}
                      placeholder="Selecionar..."
                    />
                  </div>

                  <div>
                    <label
                      style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}
                    >
                      Departamento
                    </label>
                    <Select
                      options={depOpcoes}
                      value={filtDep}
                      onChange={setFiltDep}
                      disabled={depOpcoesRaw.length <= 1}
                      placeholder="Selecionar..."
                    />
                  </div>

                  <div>
                    <label
                      style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}
                    >
                      Mês
                    </label>
                    <MultiSelect
                      options={mesOpcoes}
                      value={filtMes}
                      onChange={setFiltMes}
                      placeholder="Selecionar..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {status.error && (
            <ErrorBanner message={status.error} onRetry={fetchDados} />
          )}

          {isInitialLoad ? (
            <Spinner />
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 32,
                }}
              >
                {/* <KpiCard label="Aprovado (Despesas)" value={`R$ ${fmt(kpis.totalAprovado)}`} accent large /> */}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(175px,1fr))",
                  gap: 14,
                  marginBottom: 40,
                }}
              >
                <KpiCard label="Orçamento" value={`R$ ${fmt(kpis.totalOrc)}`} />
                <KpiCard
                  label="Realizado"
                  value={`R$ ${fmt(kpis.totalReal * -1)}`}
                />
                <KpiCard
                  label="Delta Realizado"
                  value={`R$ ${fmt(kpis.deltaReal)}`}
                />
              </div>

              {/* ── Linha 1: Orçamento por Departamento (70%) + Gastos por Grupo (30%) ── */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr fr",
                  gap: 20,
                  marginBottom: 20,
                  alignItems: "start",
                }}
              >
                {/* Orçamento · Realizado — por Departamento (70%) */}
                <div
                  style={{
                    background: C.cardBg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    padding: "24px 20px",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 20px 0",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      color: C.muted,
                      textTransform: "uppercase",
                    }}
                  >
                    Orçamento · Realizado · Aprovado — por Departamento
                  </h3>
                  {chartData.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: 60,
                        color: C.muted,
                        fontSize: 13,
                      }}
                    >
                      Nenhum dado para os filtros selecionados.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={540}>
                      <BarChart
                        data={chartData}
                        margin={{ top: 4, right: 8, left: 0, bottom: 80 }}
                        barGap={2}
                        barCategoryGap="25%"
                      >
                        <CartesianGrid vertical={false} stroke={C.border} />
                        <XAxis
                          dataKey="name"
                          tick={{ fill: C.muted, fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                          angle={-35}
                          textAnchor="end"
                          interval={0}
                        />
                        <YAxis
                          tickFormatter={fmtShort}
                          tick={{ fill: C.muted, fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                          width={72}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ fill: `${C.danger}08` }}
                        />
                        <Legend
                          wrapperStyle={{
                            marginBottom: 500,
                            fontSize: 12,
                            color: C.muted,
                          }}
                          iconType="square"
                          iconSize={10}
                        />
                        <Bar
                          dataKey="Orçamento"
                          fill={C.crimson}
                          radius={[3, 3, 0, 0]}
                          maxBarSize={55}
                        />
                        <Bar
                          dataKey="Realizado"
                          fill={C.amber}
                          radius={[3, 3, 0, 0]}
                          maxBarSize={36}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Gastos por Grupo (30%) */}
                <ChartCard
                  loading={status.loading}
                  title="Gastos por Grupo"
                  subtitle="Realizado × Orçado — por categoria superior"
                >
                  {gastosPorGrupo.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: 40,
                        color: C.muted,
                        fontSize: 13,
                      }}
                    >
                      Nenhum dado para os filtros selecionados.
                    </div>
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height={Math.max(540, gastosPorGrupo.length * 52)}
                    >
                      <BarChart
                        data={gastosPorGrupo}
                        layout="vertical"
                        margin={{ top: 4, right: 20, left: 8, bottom: 4 }}
                        barGap={3}
                      >
                        <CartesianGrid horizontal={false} stroke={C.border} />
                        <XAxis
                          type="number"
                          tickFormatter={fmtShort}
                          tick={{ fill: C.muted, fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          tick={{ fill: C.muted, fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                          width={120}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div
                                style={{
                                  background: C.tooltipBg ?? C.cardBg,
                                  border: `1px solid ${C.border}`,
                                  borderRadius: 8,
                                  padding: "10px 14px",
                                }}
                              >
                                <p
                                  style={{
                                    margin: "0 0 6px",
                                    fontSize: 11,
                                    color: C.muted,
                                    fontWeight: 700,
                                  }}
                                >
                                  {label}
                                </p>
                                {payload.map((p) => (
                                  <p
                                    key={p.dataKey}
                                    style={{
                                      margin: "2px 0",
                                      fontSize: 12,
                                      color: p.fill,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {p.name}: R$ {fmt(p.value)}
                                  </p>
                                ))}
                                {(() => {
                                  const orc =
                                    payload.find((p) => p.dataKey === "Orçado")
                                      ?.value ?? 0;
                                  const real =
                                    payload.find(
                                      (p) => p.dataKey === "Realizado",
                                    )?.value ?? 0;
                                  if (!orc) return null;
                                  const pct = (real / orc) * 100;
                                  const cor =
                                    pct >= 100
                                      ? "#FF2D55"
                                      : pct >= 80
                                        ? C.amber
                                        : C.success;
                                  return (
                                    <p
                                      style={{
                                        margin: "6px 0 0",
                                        fontSize: 11,
                                        color: cor,
                                        fontWeight: 700,
                                        borderTop: `1px solid ${C.border}`,
                                        paddingTop: 6,
                                      }}
                                    >
                                      Execução: {pct.toFixed(1)}%
                                    </p>
                                  );
                                })()}
                              </div>
                            );
                          }}
                          cursor={{ fill: `${C.crimson}08` }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 11, color: C.muted }}
                          iconType="square"
                          iconSize={10}
                        />
                        <Bar
                          dataKey="Orçado"
                          fill={C.crimson}
                          radius={[0, 3, 3, 0]}
                          maxBarSize={18}
                        />
                        <Bar
                          dataKey="Realizado"
                          fill={C.amber}
                          radius={[0, 3, 3, 0]}
                          maxBarSize={18}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              </div>
              {/* ── Linha 2: Evolução Mensal (70%) + Eficiência Gauge (30%) ── */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "7fr 3fr",
                  gap: 20,
                  marginBottom: 36,
                  alignItems: "start",
                }}
              >
                {/* Evolução Orçado vs Realizado (70%) */}
                <ChartCard
                  title="Evolução Orçado vs Realizado"
                  subtitle="Comparativo mensal entre orçamento planejado e valores lançados"
                >
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart
                      data={orcadoRealizadoPorMes}
                      margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={CGRID}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="mes"
                        tick={{ ...axisStyle, fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`}
                        tick={axisStyle}
                        axisLine={false}
                        tickLine={false}
                        width={55}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return (
                            <div
                              style={{ ...tooltipStyle, padding: "8px 12px" }}
                            >
                              <p
                                style={{
                                  color: C.text,
                                  fontWeight: 700,
                                  margin: "0 0 4px",
                                }}
                              >
                                {label}
                              </p>
                              {payload.map((p) => (
                                <p
                                  key={p.dataKey}
                                  style={{
                                    color: p.color,
                                    margin: "2px 0",
                                    fontSize: 11,
                                  }}
                                >
                                  {p.name}:{" "}
                                  <strong>
                                    {p.value.toLocaleString("pt-BR", {
                                      style: "currency",
                                      currency: "BRL",
                                    })}
                                  </strong>
                                </p>
                              ))}

                              {/* Desvio percentual inline no tooltip */}
                              {payload.length === 2 &&
                                (() => {
                                  const orc =
                                    payload.find((p) => p.dataKey === "orcado")
                                      ?.value ?? 0;
                                  const real =
                                    payload.find(
                                      (p) => p.dataKey === "realizado",
                                    )?.value ?? 0;
                                  if (!orc) return null;
                                  const desvio = (
                                    ((real - orc) / orc) *
                                    100
                                  ).toFixed(1);
                                  const cor =
                                    real > orc ? "#ef4444" : "#22c55e"; // vermelho se estourou
                                  return (
                                    <p
                                      style={{
                                        color: cor,
                                        margin: "4px 0 0",
                                        fontSize: 11,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Desvio: {desvio > 0 ? "+" : ""}
                                      {desvio}%
                                    </p>
                                  );
                                })()}
                            </div>
                          );
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />

                      {/* Linha tracejada = Orçado */}
                      <Line
                        type="monotone"
                        dataKey="orcado"
                        name="Orçado"
                        stroke={CPREV}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ r: 3 }}
                      />

                      {/* Linha sólida = Realizado */}
                      <Line
                        type="monotone"
                        dataKey="realizado"
                        name="Realizado"
                        stroke={CREAL}
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Eficiência Orçamentária — Gauge (30%) */}
                <ChartCard
                  height="200%"
                  loading={status.loading}
                  title="Eficiência Orçamentária"
                  subtitle="Realizado / Orçado (dados filtrados)"
                >
                  {eficiencia == null ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: 40,
                        color: C.muted,
                        fontSize: 13,
                      }}
                    >
                      Sem orçamento para calcular.
                    </div>
                  ) : (
                    <GaugeChart pct={eficiencia} C={C} />
                  )}
                </ChartCard>
              </div>

              {/* Tabela */}
              <div
                style={{
                  background: C.cardBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: `1px solid ${C.border}`,
                        background: C.panelBg,
                      }}
                    >
                      {["Departamento", "Orçamento", "Realizado", "Delta"].map(
                        (col) => (
                          <th
                            key={col}
                            style={{
                              padding: "13px 40px",
                              textAlign:
                                col === "Departamento" ? "left" : "right",
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.12em",
                              color: C.muted,
                              textTransform: "uppercase",
                            }}
                          >
                            {col}
                          </th>
                        ),
                      )}
                      <th
                        style={{
                          padding: "13px 20px",
                          textAlign: "center",
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.12em",
                          color: C.muted,
                          textTransform: "uppercase",
                          width: 90,
                        }}
                      >
                        % Exec.
                      </th>
                      <th
                        style={{
                          padding: "13px 16px",
                          textAlign: "center",
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.12em",
                          color: C.muted,
                          textTransform: "uppercase",
                          width: 60,
                        }}
                      >
                        Comparativo
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {merged.map((row, i) => (
                      <tr
                        key={row.Departamento}
                        style={{
                          borderBottom: `1px solid ${C.border}`,
                          background: i % 2 === 0 ? "transparent" : C.rowAlt88,
                          transition: "background 0.12s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = `${C.crimson}18`)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            i % 2 === 0 ? "transparent" : C.rowAlt88)
                        }
                      >
                        <td
                          style={{
                            padding: "11px 20px",
                            color: C.text,
                            fontWeight: 500,
                          }}
                        >
                          {row.Departamento}
                        </td>
                        <td
                          style={{
                            padding: "11px 20px",
                            textAlign: "right",
                            color: C.text,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 6,
                            }}
                          >
                            <span>
                              {row.Orçamento != null ? fmt(row.Orçamento) : "-"}
                            </span>
                            {row.Orçamento != null && (
                              <button
                                onClick={() =>
                                  setDrawerOrcDep(row.Departamento)
                                }
                                title={`Ver detalhamento ${row.Departamento}`}
                                style={{
                                  background: "transparent",
                                  border: `1px solid ${C.border}`,
                                  borderRadius: 5,
                                  color: C.muted,
                                  cursor: "pointer",
                                  fontSize: 11,
                                  width: 22,
                                  height: 22,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  transition:
                                    "border-color 0.15s, color 0.15s, background 0.15s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = C.crimson;
                                  e.currentTarget.style.color = C.crimson;
                                  e.currentTarget.style.background = `${C.crimson}15`;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = C.border;
                                  e.currentTarget.style.color = C.muted;
                                  e.currentTarget.style.background =
                                    "transparent";
                                }}
                              >
                                ≡
                              </button>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "11px 20px",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: 600,
                            color:
                              row.Realizado == null
                                ? C.muted
                                : row.Realizado < 0
                                  ? C.danger
                                  : C.success,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 6,
                            }}
                          >
                            <span>
                              {row.Realizado != null
                                ? fmt(row.Realizado * -1)
                                : "-"}
                            </span>
                            {row.Realizado != null && (
                              <button
                                onClick={() => setDrawerDep(row.Departamento)}
                                title={`Ver detalhamento ${row.Departamento}`}
                                style={{
                                  background: "transparent",
                                  border: `1px solid ${C.border}`,
                                  borderRadius: 5,
                                  color: C.muted,
                                  cursor: "pointer",
                                  fontSize: 11,
                                  width: 22,
                                  height: 22,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  transition:
                                    "border-color 0.15s, color 0.15s, background 0.15s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = C.crimson;
                                  e.currentTarget.style.color = C.crimson;
                                  e.currentTarget.style.background = `${C.crimson}15`;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = C.border;
                                  e.currentTarget.style.color = C.muted;
                                  e.currentTarget.style.background =
                                    "transparent";
                                }}
                              >
                                ≡
                              </button>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "11px 20px",
                            textAlign: "right",
                            color: C.text,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {(() => {
                            const delta =
                              (row.Orçamento ?? 0) + (row.Realizado ?? 0);
                            if (row.Orçamento == null && row.Realizado == null)
                              return "-";
                            return (
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: delta < 0 ? C.danger : C.success,
                                }}
                              >
                                {fmt(delta)}
                              </span>
                            );
                          })()}
                        </td>
                        <td
                          style={{ padding: "11px 20px", textAlign: "center" }}
                        >
                          {(() => {
                            const orc = row.Orçamento;
                            const real = row.Realizado;
                            if (orc == null || orc === 0 || real == null)
                              return <span style={{ color: C.muted }}>—</span>;
                            const pct = (Math.abs(real) / Math.abs(orc)) * 100;
                            const cor =
                              pct >= 100
                                ? "#FF2D55"
                                : pct >= 80
                                  ? C.amber
                                  : pct >= 60
                                    ? "#F0C040"
                                    : C.success;
                            const bg =
                              pct >= 100
                                ? "#FF2D5515"
                                : pct >= 80
                                  ? `${C.amber}18`
                                  : pct >= 60
                                    ? "#F0C04015"
                                    : `${C.success}12`;
                            return (
                              <span
                                style={{
                                  display: "inline-block",
                                  minWidth: 60,
                                  padding: "3px 8px",
                                  borderRadius: 20,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: cor,
                                  background: bg,
                                  fontVariantNumeric: "tabular-nums",
                                  border: `1px solid ${cor}44`,
                                }}
                              >
                                {pct.toFixed(1)}%
                              </span>
                            );
                          })()}
                        </td>
                        <td
                          style={{ padding: "11px 16px", textAlign: "center" }}
                        >
                          <button
                            onClick={() =>
                              setDrawerDetalheDep(row.Departamento)
                            }
                            title={`Ver comparativo Orçado vs Realizado por Grupo — ${row.Departamento}`}
                            style={{
                              background: "transparent",
                              border: `1px solid ${C.border}`,
                              borderRadius: 6,
                              color: C.muted,
                              cursor: "pointer",
                              fontSize: 13,
                              width: 28,
                              height: 28,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition:
                                "border-color 0.15s, color 0.15s, background 0.15s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = C.crimson;
                              e.currentTarget.style.color = C.crimson;
                              e.currentTarget.style.background = `${C.crimson}15`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = C.border;
                              e.currentTarget.style.color = C.muted;
                              e.currentTarget.style.background = "transparent";
                            }}
                          >
                            ⊕
                          </button>
                        </td>
                      </tr>
                    ))}
                    {/* Linha de total */}
                    <tr
                      style={{
                        borderTop: `2px solid ${C.border}`,
                        background: C.panelBg,
                      }}
                    >
                      <td
                        style={{
                          padding: "13px 20px",
                          fontWeight: 800,
                          color: C.text,
                        }}
                      >
                        Total geral
                      </td>
                      <td
                        style={{
                          padding: "13px 40px",
                          textAlign: "right",
                          fontWeight: 800,
                          fontVariantNumeric: "tabular-nums",
                          color: C.text,
                        }}
                      >
                        {fmt(kpis.totalOrc)}
                      </td>
                      <td
                        style={{
                          padding: "13px 40px",
                          textAlign: "right",
                          fontWeight: 800,
                          fontVariantNumeric: "tabular-nums",
                          color: kpis.totalReal < 0 ? C.danger : C.success,
                        }}
                      >
                        {fmt(kpis.totalReal * -1)}
                      </td>
                      <td
                        style={{
                          padding: "13px 20px",
                          textAlign: "right",
                          fontWeight: 800,
                          fontVariantNumeric: "tabular-nums",
                          color: kpis.deltaReal < 0 ? C.danger : C.success,
                        }}
                      >
                        {fmt(kpis.deltaReal)}
                      </td>
                      <td
                        style={{
                          padding: "13px 40px",
                          textAlign: "right",
                          fontWeight: 800,
                        }}
                      >
                        {(() => {
                          const pct =
                            kpis.totalOrc !== 0
                              ? (Math.abs(kpis.totalReal) /
                                  Math.abs(kpis.totalOrc)) *
                                100
                              : null;
                          if (pct == null)
                            return <span style={{ color: C.muted }}>—</span>;
                          const cor =
                            pct >= 100
                              ? "#FF2D55"
                              : pct >= 80
                                ? C.amber
                                : pct >= 60
                                  ? "#F0C040"
                                  : C.success;
                          return (
                            <span
                              style={{
                                color: cor,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {pct.toFixed(1)}%
                            </span>
                          );
                        })()}
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>

              <p
                style={{
                  marginTop: 14,
                  fontSize: 11,
                  color: C.muted,
                  textAlign: "right",
                }}
              >
                {lancamentos.length} lançamentos · {orcamento.length} orçamentos
                · {despesas.length} despesas
              </p>
            </>
          )}
        </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}
